import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';

const DeleteWorkflowModal = ({
  isOpen,
  workflowName,
  onCancel,
  onConfirm,
  confirming = false,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !confirming) {
        onCancel?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, confirming, onCancel]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 p-4"
      data-testid="delete-workflow-modal-backdrop"
      data-workflow-editor-ignore-pan
      onClick={confirming ? undefined : onCancel}
      role="presentation"
    >
      <div
        className="relative w-full max-w-md rounded-xl bg-white shadow-2xl"
        data-testid="delete-workflow-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={confirming}
          className="absolute right-4 top-4 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
          aria-label="Close"
          data-testid="delete-workflow-close"
        >
          <X size={18} />
        </button>

        <div className="p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600">
            <Trash2 size={18} />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-slate-900">
            Are you sure you want to delete this workflow?
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            This action cannot be undone.
          </p>
          {workflowName ? (
            <p className="mt-3 text-sm font-medium text-slate-700">{workflowName}</p>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={confirming}
            data-testid="delete-workflow-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={onConfirm}
            disabled={confirming}
            data-testid="delete-workflow-confirm"
          >
            {confirming ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DeleteWorkflowModal;
